import { useTranslation } from 'react-i18next';

import Tooltip, { TooltipRows } from '@/components/atoms/Tooltip';
import type { PenWarning } from '@/domain/feedingDashboard';

import { dashboardLocale } from '../dashboardLocale';
import type { SystemLabels } from '../systemLabel';
import { WarningIcon } from '../WarningMarker/WarningMarker';

import { warningConditions } from './penWarningText';

import styles from './PenComparisonTable.module.scss';

interface PenWarningMarkerProps {
    /** The conditions the pen meets; the marker is only rendered when there is one. */
    warnings: PenWarning[];
    system: SystemLabels;
}

/**
 * The row marker of the warning state (IPS 2.6.2). The icon carries the conditions in its
 * accessible name as well as in the tooltip, so the state is never colour alone.
 */
export function PenWarningMarker(props: PenWarningMarkerProps) {
    const { warnings, system } = props;
    const { t, i18n } = useTranslation();
    const conditions = warningConditions(warnings, system, t, dashboardLocale(i18n));
    // The locale's own list punctuation rather than a separator hardcoded here.
    const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(conditions);

    return (
        <Tooltip
            content={
                <TooltipRows>
                    <strong>{t('feedingModule.table.warning.title')}</strong>
                    <ul className={styles.markerConditions}>
                        {conditions.map((condition) => (
                            <li key={condition}>{condition}</li>
                        ))}
                    </ul>
                </TooltipRows>
            }
            placement="right"
            layout="fit">
            <WarningIcon label={t('feedingModule.table.warning.label', { conditions: list })} />
        </Tooltip>
    );
}
